'use client';

import { useEffect } from 'react';

/**
 * Kicks off Datadog RUM/Logs initialization after hydration. The observability
 * module (and the Datadog SDKs it dynamically imports) is loaded via a dynamic
 * import here — same pattern as the lazy wallet connectors in providers/ — so
 * monitoring never adds to any route's first-load chunk. The SDKs themselves
 * load at the browser's next idle moment, after the wallet bootstrap, and an
 * error reported meanwhile waits for them. When the Datadog env vars are
 * absent (local dev, CI) the init resolves as a no-op.
 *
 * Until the SDKs are up nothing catches an uncaught error or an unhandled
 * rejection on its own: the wait is up to three seconds plus the download,
 * and for the rest of the session when the SDKs fail to load. So this
 * listens for both until monitoring is up (`monitoringReady`, which a later
 * retried start also settles) and forwards them to `reportGlobalError`. That
 * holds a report until monitoring has started, and itself retries a failed
 * start, with its own pause between tries. Once monitoring is up the SDKs
 * catch these themselves, and the listeners go, so nothing is reported
 * twice. A cross-origin "Script error." is opaque and is skipped; any other
 * event without an error object is reported from its message and location.
 */
/**
 * An error for an event the browser raised without an error object. A
 * cross-origin script's failure is opaque ("Script error." with no source)
 * and says nothing, so it is null. Anything else still names what broke and
 * where: the file's path only, never its query string.
 */
function errorFromEvent(event: ErrorEvent): Error | null {
  const message = event.message?.trim();
  if (!message) return null;
  if (/^script error\.?$/i.test(message) && !event.filename) return null;
  let where = '';
  if (event.filename) {
    try {
      where = ` (${new URL(event.filename, window.location.href).pathname}:${event.lineno}:${event.colno})`;
    } catch {
      where = '';
    }
  }
  return new Error(`${message}${where}`);
}

export function ObservabilityInit() {
  useEffect(() => {
    // Loaded once; the start and every early report share it.
    const observability = import('@/lib/observability');

    const report = (error: unknown, scope: string) => {
      void observability
        .then((mod) =>
          mod.reportGlobalError(error, {
            scope,
            route: window.location.pathname
          })
        )
        .catch(() => {
          // Reporting must never become a second failure.
        });
    };
    const onError = (event: ErrorEvent) => {
      if (event.error != null) {
        report(event.error, 'early-window-error');
        return;
      }
      const reported = errorFromEvent(event);
      if (reported) report(reported, 'early-window-error');
    };
    const onRejection = (event: PromiseRejectionEvent) =>
      report(event.reason, 'early-unhandled-rejection');

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    let listening = true;
    const stopListening = () => {
      if (!listening) return;
      listening = false;
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };

    observability
      .then((mod) => {
        mod.initObservability({ afterIdle: true }).catch(() => {
          // Monitoring failing to load must never affect the app; nothing
          // to report it to — RUM is the thing that failed. The listeners
          // stay, and the next report tries the start again.
        });
        return mod.monitoringReady();
      })
      // The module itself did not load: nothing could be reported from
      // here either, so stop listening.
      .then(stopListening, stopListening);

    return stopListening;
  }, []);

  return null;
}
